import * as React from 'react';
import { styled, css } from '@mui/material/styles';
import Radio, { RadioProps } from '@mui/material/Radio';
import RadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import { color } from '../../../styles';
import { FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
  data: {
    key: string | number | boolean;
    value: string;
    icon?: string;
    iconComponent?: React.FC<{ fillColor: string }>;
    isNarrowText?: boolean;
  }[];
  isDisabled?: boolean;
  isRow?: boolean;
  newValue?: string | number | boolean | null;
  mode?: 'button' | 'radio'; // 同じボタンを押した時に値をクリアするかしないか
  onChange: (value: string | number | boolean | null) => void;
}

interface RadioWrapperProps {
  isRow: boolean;
}

const StyledRadioGroup = styled(({ isRow, ...props }: RadioWrapperProps & RadioGroupProps) => (
  <RadioGroup {...props} />
))`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: ${({ isRow }) => (isRow ? 'row' : 'column')};
  ${({ isRow }) =>
    isRow
      ? css`
          &:before {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            height: 6px;
            background: ${color.thin_gray};
            transform: translateY(-50%);
          }
        `
      : css`
          height: 100%;
        `}
`;

const StyledRadio = styled(({ isRow, ...props }: RadioWrapperProps & RadioProps) => <Radio {...props} />)`
  min-width: 50px;
  height: 49px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2px;
  background: ${color.white};
  padding: 7px;
  margin-right: ${({ isRow }) => (isRow ? '4px' : 0)};
  margin-bottom: ${({ isRow }) => (!isRow ? '4px' : 0)};
  border-radius: 8px;
  border: 1px solid ${color.border_gray};
  flex: 1;
  &:hover {
    color: ${color.white};

    background: ${color.gray};
  }
  &.Mui-checked {
    color: ${color.white};
    background: ${color.dark_gray};
  }
  &.Mui-disabled {
    background: ${color.button_gray};
  }
  &:last-child {
    margin-right: 0;
    margin-bottom: 0;
  }
`;

const StyledText = (isNarrow: boolean) => css`
  font-size: 12px;
  line-height: 1;
  transform: ${isNarrow ? 'scale(0.5, 1)' : 'none'};
  width: fit-content;
  white-space: nowrap;
`;

export const CareRecordButtonGroup = React.memo(
  ({ data: displayList, newValue = '', isRow = true, isDisabled = false, mode = 'button', onChange }: Props) => {
    const { i18n } = useTranslation();
    const [value, setValue] = React.useState<string | number | boolean | null>(null);
    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      onChange(e.target.value);
    }, []);

    const handleClickItem = (e: React.MouseEvent<HTMLElement>) => {
      const selected = (e.currentTarget as HTMLInputElement).querySelector('input')?.value;
      if (mode === 'button' && selected === String(value)) {
        setValue(null);
        onChange(null);
      }
    };

    const iconColor = (isDisabled: boolean) => {
      if (isDisabled) {
        return 'white';
      } else {
        return color.brown;
      }
    };

    const radioContent = (
      text: string,
      icon?: string,
      IconComponent?: React.FC<{ fillColor: string }>,
      isDisabled?: boolean,
      isNarrowText?: boolean
    ) => {
      // 日本語以外かつiconに画像パスが渡っているときは、画像を表示する
      if (icon && !i18n.language.startsWith('ja')) {
        return <img src={icon} alt={text} />;
      }
      // iconComponentにコンポーネントが渡っているときは、コンポーネントを表示する
      else if (IconComponent) {
        return (
          <>
            <IconComponent fillColor={iconColor(isDisabled ?? false)} />
            {i18n.language.startsWith('ja') && <span css={StyledText(isNarrowText ?? false)}>{text}</span>}
          </>
        );
      } else {
        return <>{text}</>;
      }
    };

    React.useEffect(() => {
      setValue(newValue);
    }, [newValue]);

    return (
      <FormGroup sx={{ width: '100%' }}>
        <StyledRadioGroup isRow={isRow} value={value ?? ''} onChange={handleChange}>
          {displayList.map((radio, index) => {
            return (
              <StyledRadio
                key={index}
                disabled={isDisabled}
                value={radio.key}
                color='default'
                icon={radioContent(radio.value, radio.icon, radio.iconComponent, isDisabled, radio.isNarrowText)}
                checkedIcon={radioContent(radio.value, radio.icon, radio.iconComponent, isDisabled, radio.isNarrowText)}
                isRow={isRow}
                onClick={handleClickItem}
              />
            );
          })}
        </StyledRadioGroup>
      </FormGroup>
    );
  }
);
